import { useState, useMemo, useEffect } from 'react'
import { Player, PlayerRating as PlayerRatingType } from '../types'
import { useAuthStore } from '../stores'
import { apiClient } from '../utils/api'
import { Star, MessageSquare, Loader2, CheckCircle2, AlertCircle } from 'lucide-react'

interface PlayerRatingProps {
  player: Player
}

function PlayerRating({ player }: PlayerRatingProps) {
  const { user } = useAuthStore()
  const [ratings, setRatings] = useState<PlayerRatingType[]>([])
  const [loading, setLoading] = useState(true)
  const [myRating, setMyRating] = useState<number>(0)
  const [hoverRating, setHoverRating] = useState<number>(0)
  const [comment, setComment] = useState('')
  const [saving, setSaving] = useState(false)
  const [success, setSuccess] = useState('')
  const [error, setError] = useState('')
  const [existingRatingId, setExistingRatingId] = useState<string | null>(null)

  const canRate = user && (user.role === 'scout' || user.role === 'club' || user.role === 'admin')

  const averageRating = useMemo(() => {
    if (ratings.length === 0) return 0
    const sum = ratings.reduce((acc, r) => acc + r.rating, 0)
    return Math.round((sum / ratings.length) * 10) / 10
  }, [ratings])

  const ratingDistribution = useMemo(() => {
    const dist: Record<number, number> = {}
    for (let i = 1; i <= 10; i++) dist[i] = 0
    ratings.forEach(r => {
      const bucket = Math.floor(r.rating)
      dist[bucket] = (dist[bucket] || 0) + 1
    })
    return dist
  }, [ratings])

  useEffect(() => {
    if (!player.id) return
    setLoading(true)
    Promise.all([
      apiClient.getPlayerRatings(player.id),
      canRate && user ? apiClient.getPlayerRatingByRater(player.id, user.id) : Promise.resolve(null),
    ]).then(([ratingsData, myRatingData]) => {
      setRatings(ratingsData)
      if (myRatingData) {
        setMyRating(myRatingData.rating)
        setComment(myRatingData.comment || '')
        setExistingRatingId(myRatingData.id)
      }
      setLoading(false)
    }).catch(() => setLoading(false))
  }, [player.id, user, canRate])

  const handleSubmit = async () => {
    if (!user || !canRate || myRating < 1 || myRating > 10) return
    setSaving(true)
    setError('')
    try {
      const result = await apiClient.upsertPlayerRating({
        player_id: player.id,
        rater_id: user.id,
        rater_role: user.role as 'scout' | 'club' | 'admin',
        rating: myRating,
        comment: comment.trim() || undefined,
      })
      setExistingRatingId(result.id)
      // Refresh ratings
      const updated = await apiClient.getPlayerRatings(player.id)
      setRatings(updated)
      setSuccess(existingRatingId ? 'Rating updated!' : 'Rating submitted!')
      setTimeout(() => setSuccess(''), 3000)
    } catch (err: any) {
      setError(err.message || 'Failed to save rating')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="text-green-500 animate-spin" size={24} />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Average Rating Display */}
      <div className="bg-[#161b22] border border-gray-800 rounded-2xl p-6">
        <div className="flex items-center gap-6">
          <div className="text-center">
            <p className="text-5xl font-black text-white">{averageRating > 0 ? averageRating.toFixed(1) : '—'}</p>
            <p className="text-[10px] text-gray-500 uppercase tracking-widest mt-1">
              {ratings.length} rating{ratings.length !== 1 ? 's' : ''}
            </p>
          </div>
          <div className="flex-1 space-y-1">
            {[10, 9, 8, 7, 6, 5, 4, 3, 2, 1].map(n => {
              const count = ratingDistribution[n] || 0
              const pct = ratings.length > 0 ? (count / ratings.length) * 100 : 0
              return (
                <div key={n} className="flex items-center gap-2">
                  <span className="text-[10px] text-gray-500 w-3 text-right">{n}</span>
                  <div className="flex-1 h-1.5 bg-gray-800 rounded-full overflow-hidden">
                    <div className="h-full bg-green-500 rounded-full" style={{ width: `${pct}%` }} />
                  </div>
                  <span className="text-[10px] text-gray-600 w-4 text-right">{count}</span>
                </div>
              )
            })}
          </div>
          <div className="flex flex-col items-center gap-1">
            {[1, 2, 3, 4, 5].map(s => (
              <Star
                key={s}
                size={16}
                className={s <= Math.round(averageRating / 2) ? 'text-yellow-400 fill-yellow-400' : 'text-gray-700'}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Rate This Player (scouts/clubs only) */}
      {canRate && (
        <div className="bg-[#161b22] border border-gray-800 rounded-2xl p-6">
          <h3 className="text-sm font-bold text-white uppercase tracking-wider mb-4">
            {existingRatingId ? 'Update Your Rating' : 'Rate This Player'}
          </h3>

          {success && (
            <div className="flex items-center gap-2 p-3 bg-green-900/20 border border-green-700/30 rounded-xl text-green-400 text-xs font-bold mb-4">
              <CheckCircle2 size={16} />
              {success}
            </div>
          )}
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-900/20 border border-red-700/30 rounded-xl text-red-400 text-xs font-bold mb-4">
              <AlertCircle size={16} />
              {error}
            </div>
          )}

          <div className="space-y-4">
            {/* Star Selector */}
            <div>
              <p className="text-[10px] text-gray-500 uppercase tracking-widest mb-2">Rating (1.0 - 10.0)</p>
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => (
                  <button
                    key={n}
                    type="button"
                    onMouseEnter={() => setHoverRating(n)}
                    onMouseLeave={() => setHoverRating(0)}
                    onClick={() => setMyRating(n)}
                    className={`w-10 h-10 rounded-xl text-sm font-bold transition ${
                      n <= (hoverRating || myRating)
                        ? 'bg-green-500 text-black'
                        : 'bg-white/5 text-gray-500 hover:bg-white/10'
                    }`}
                  >
                    {n}
                  </button>
                ))}
              </div>
              {myRating > 0 && (
                <p className="text-xs text-green-400 mt-2 font-bold">
                  {myRating >= 9 ? '⭐ Elite talent' :
                   myRating >= 8 ? 'High potential' :
                   myRating >= 7 ? 'Strong player' :
                   myRating >= 5 ? 'Solid prospect' : 'Developing player'}
                </p>
              )}
            </div>

            {/* Comment */}
            <div>
              <label className="block text-[10px] text-gray-500 uppercase tracking-widest mb-1">
                <MessageSquare size={10} className="inline mr-1" />
                Comment (optional)
              </label>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Brief assessment of the player..."
                rows={3}
                className="w-full px-4 py-2.5 bg-white/5 border border-white/5 rounded-xl text-white placeholder-gray-600 focus:ring-2 focus:ring-green-500/20 focus:border-green-500 outline-none transition text-sm font-bold resize-none"
              />
            </div>

            <button
              onClick={handleSubmit}
              disabled={saving || myRating < 1}
              className="flex items-center gap-2 px-6 py-2.5 bg-green-500 border border-green-500 rounded-xl text-black hover:bg-green-400 transition text-xs font-bold uppercase tracking-widest disabled:opacity-50"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Star size={14} />}
              {saving ? 'Saving...' : existingRatingId ? 'Update Rating' : 'Submit Rating'}
            </button>
          </div>
        </div>
      )}

      {/* Recent Ratings */}
      {ratings.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-[10px] text-gray-500 uppercase tracking-widest">Recent Ratings</h3>
          {ratings.slice(0, 10).map(r => (
            <div key={r.id} className="bg-[#161b22] border border-gray-800 rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-green-500/10 flex items-center justify-center">
                    <span className="text-xs font-bold text-green-400">{r.rating}</span>
                  </div>
                  <div>
                    <p className="text-xs font-bold text-white capitalize">{r.rater_role}</p>
                    <p className="text-[10px] text-gray-500">
                      {r.created_at ? new Date(r.created_at).toLocaleDateString() : ''}
                    </p>
                  </div>
                </div>
                <div className="flex gap-0.5">
                  {[1, 2, 3, 4, 5].map(s => (
                    <Star
                      key={s}
                      size={10}
                      className={s <= Math.round(r.rating / 2) ? 'text-yellow-400 fill-yellow-400' : 'text-gray-700'}
                    />
                  ))}
                </div>
              </div>
              {r.comment && (
                <p className="text-xs text-gray-400 mt-2 italic">{r.comment}</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default PlayerRating
